import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { donations } from 'src/backend';
import { useParams, useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { PATH_DASHBOARD } from 'src/routes/paths';
import moment from 'moment-timezone';
import { formatDisplayDate } from 'careit';
import { useTranslation } from 'react-i18next';
import { useCancellationReasonOptions } from 'src/hooks/useCancelationReason';
import { parseJsonArray } from 'src/utils/json';
import useAuth from 'src/hooks/useAuth';
import emitDonationUpdate from 'src/utils/emitDonationUpdate';

// How cancelling affects a partial donation's portions.
const CancelImpact = {
  // Nothing claimed (or non-partial donation).
  NO_CLAIMS: 'NO_CLAIMS',
  // Claimed, but nothing rescued yet.
  CLAIMS_NOT_RESCUED: 'CLAIMS_NOT_RESCUED',
  // Some portions already rescued.
  SOME_RESCUED: 'SOME_RESCUED',
};

export function getDonationCancellationServiceName({
  type,
  isFoodRun,
  isRecurring,
}) {
  if (type === 'recurring') return 'cancelRecurringDonations';
  if (isFoodRun) return 'cancelFoodRunInstance';
  if (isRecurring) return 'cancelDonationInstance';
  return 'cancelDonation';
}

// Summarize what cancelling a partial donation will do.
function getCancelImpact({ donation, allRescues }) {
  if (!donation?.is_partial) {
    return {
      type: CancelImpact.NO_CLAIMS,
      completedCount: 0,
      claimedToCancelCount: 0,
      remainingOpenWeight: 0,
    };
  }

  const claimedRescues = allRescues.filter((r) => r.id);
  const completedCount = claimedRescues.filter(
    (r) => r.rescue_status === 'COMPLETED'
  ).length;
  const claimedToCancelCount = claimedRescues.length - completedCount;
  const remainingOpenWeight = parseJsonArray(
    donation.unassigned_food_types
  ).reduce((sum, ft) => sum + (Number(ft.partial_weight_reserved) || 0), 0);

  let type = CancelImpact.NO_CLAIMS;
  if (completedCount > 0) type = CancelImpact.SOME_RESCUED;
  else if (claimedToCancelCount > 0) type = CancelImpact.CLAIMS_NOT_RESCUED;

  return { type, completedCount, claimedToCancelCount, remainingOpenWeight };
}

export default function ConfirmModal({
  title: title_,
  open,
  handleCancel,
  donation,
  allRescues = [],
  type = 'onetime', // cancel a single instance or all instances
  isFoodRun = false,
}) {
  const { t } = useTranslation('translation');
  const title = title_ ?? t('donations.are-you-sure');
  const params = useParams();
  const history = useHistory();
  const [selectedReason, setSelectedReason] = useState(null);
  const [loading, setLoading] = useState(false);
  const [otherReason, setOtherReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const reasons = useCancellationReasonOptions({ open });

  // Show the impact warning for any partial donation cancel (not food runs).
  const showPartialImpact = Boolean(donation?.is_partial && !isFoodRun);
  const impact = getCancelImpact({ donation, allRescues });
  const needsAcknowledgement =
    showPartialImpact && impact.type !== CancelImpact.NO_CLAIMS;

  const handleCancelDonation = () => {
    setLoading(true);
    const service =
      donations[
        getDonationCancellationServiceName({
          type,
          isFoodRun,
          isRecurring: donation.is_recurring,
        })
      ];
    service({
      parentId: donation.parent_id,
      donationId: params.id,
      reasonId: selectedReason,
      otherReason,
    })
      .then(() => {
        // Pattern cancels still carry the instance id so the backend can locate it.
        emitDonationUpdate({
          donationId: params.id,
          patternId: donation.parent_id,
          teamId: user.team_id,
          isFoodRun,
        });
        history.push(PATH_DASHBOARD.donations);
      })
      .catch(() =>
        enqueueSnackbar(t('form.errors.generic'), {
          variant: 'error',
        })
      )
      .finally(() => {
        setLoading(false);
        setOtherReason('');
        setSelectedReason(null);
        setAcknowledged(false);
        handleCancel();
      });
  };
  const entity = isFoodRun ? 'run' : 'donation';
  const date = formatDisplayDate(
    moment().tz(donation.timezone_id ?? 'America/Los_Angeles'),
    'dateTime'
  );

  const cancelFutureInstances = {
    donation: t('donations.this-will-cancel-all-incomplete-future-instances', {
      context: 'donation',
      date,
    }),
    run: t('donations.this-will-cancel-all-incomplete-future-instances', {
      context: 'run',
      date,
    }),
  };

  const cancelThisOnly = {
    donation: t('donations.this-will-cancel-this-only', {
      context: 'donation',
    }),
    run: t('donations.this-will-cancel-this-only', { context: 'run' }),
  };

  const areYouSure = {
    donation: t('donations.are-you-sure-you-want-to-cancel', {
      context: 'donation',
    }),
    run: t('donations.are-you-sure-you-want-to-cancel', {
      context: 'run',
    }),
  };
  const cancelDonation = {
    donation: t('donations.cancel-donation'),
    run: t('donations.cancel-run'),
  };
  const keepDonation = {
    donation: t('donations.keep', { context: 'donation' }),
    run: t('donations.keep', { context: 'run' }),
  };
  return (
    <Dialog open={open} onClose={handleCancel}>
      <DialogTitle>{title}</DialogTitle>

      <DialogContent>
        <Typography>
          {type === 'recurring'
            ? cancelFutureInstances[entity]
            : donation.is_recurring
              ? cancelThisOnly[entity]
              : areYouSure[entity]}
        </Typography>
        {showPartialImpact &&
          impact.type === CancelImpact.CLAIMS_NOT_RESCUED && (
            <Typography sx={{ mt: 2 }}>
              {t('donations.cancel-partial-claimed', {
                claimedCount: impact.claimedToCancelCount,
                weight: impact.remainingOpenWeight,
              })}
            </Typography>
          )}
        {showPartialImpact && impact.type === CancelImpact.SOME_RESCUED && (
          <Typography sx={{ mt: 2 }}>
            {t('donations.cancel-partial-completed', {
              completedCount: impact.completedCount,
              claimedCount: impact.claimedToCancelCount,
              weight: impact.remainingOpenWeight,
            })}
          </Typography>
        )}
      </DialogContent>
      <Box px={5}>
        <FormControl fullWidth>
          <InputLabel id="reason-select">{t('donations.reason')}</InputLabel>
          <Select
            labelId="reason-select"
            id="reason-select"
            value={selectedReason}
            label={t('donations.reason')}
            onChange={(e) => {
              setSelectedReason(e.target.value);
            }}
          >
            {reasons.map((item) => (
              <MenuItem value={item.id} key={item.id}>
                {item.reason}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Box py={2}>
          {selectedReason === 'custom' ? (
            <TextField
              id="custom-reason"
              label={t('donations.specify-reason')}
              multiline
              rows={4}
              value={otherReason}
              onChange={(e) => setOtherReason(e.target.value)}
              fullWidth
            />
          ) : (
            ''
          )}
        </Box>
        {needsAcknowledgement && (
          <FormControlLabel
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
              />
            }
            label={t('donations.cancel-partial-acknowledge')}
          />
        )}
      </Box>

      <DialogActions>
        <Button onClick={handleCancel}>{keepDonation[entity]}</Button>
        <LoadingButton
          onClick={handleCancelDonation}
          variant="outlined"
          loading={loading}
          disabled={
            !selectedReason ||
            (selectedReason === 'custom' && !otherReason) ||
            (needsAcknowledgement && !acknowledged)
          }
        >
          {cancelDonation[entity]}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}
